/**
 * Transfer Products → Typesense document builder.
 *
 * Reads from core.product_item (joined with transfer_request for certificate_number)
 * and builds search documents for the transfer-products Typesense collection.
 */
import {
  SQSClient,
  SendMessageCommand,
  MessageAttributeValue,
} from "@aws-sdk/client-sqs"
import { coreProductItem, coreContainer, transferRequest } from "@tc/drizzle-v2"
import { eq, inArray } from "drizzle-orm"
import { DbTransaction } from "@tc/middy-decorators"
import { ProductAttributes } from "../types/core"
import { fetchNzhscFromClassification } from "../utils/classification-enrichment"
import { batchedInQuery } from "../utils/batched-in-query"

const sqs_client = new SQSClient({})
const queue_url = process.env["TRANSFER_PRODUCTS_QUEUE_URL"]

const extractId = (obj: unknown): string =>
  obj && typeof obj === "object" && "id" in obj ? String(obj.id) : ""

const extractName = (obj: unknown): string =>
  obj && typeof obj === "object" && "name" in obj ? String(obj.name) : ""

interface TransferProductDoc {
  id: string
  item_description: string
  net_weight?: string | null
  net_volume?: string | null
  eligibility_Countries?: string[]
  container_number?: string | null
  container_seals?: string[]
  NZHSC_code?: string | null
  productionBatch_ids?: string[]
  package_quantity?: string | null
  package_type?: string | null
  item_marks?: string[]
  transfer_id: string
  certificate_number: string
  unofficial_commercial_ref?: string | null
  comments?: string | null
  sequence_number?: number | null
  updated_at?: string | null
  is_exhausted?: boolean | null
}

const buildTransferProductDoc = (
  product: {
    id: string
    description: unknown
    attributes: unknown
    packaging: unknown
    eligibilityCountries: unknown
    productReference: string | null
    productStatement: string | null
    groupSequence: number | null
    updatedAt: Date
    isExhausted?: boolean | null
  },
  transferId: string,
  certificateNumber: string,
  container?: { containerNumber: string | null; seals: unknown } | null,
  nzhscByProduct?: Map<string, { id: string | null; name: string | null }>,
): TransferProductDoc => {
  const attrs: ProductAttributes =
    product.attributes && typeof product.attributes === "object"
      ? (product.attributes as ProductAttributes)
      : {}
  // Enrich with NZHSCode from classification table if not already present
  if (!attrs.NZHSCode && nzhscByProduct?.has(product.id)) {
    attrs.NZHSCode = nzhscByProduct.get(
      product.id,
    ) as unknown as ProductAttributes["NZHSCode"]
  }
  const netWeight = attrs.netWeight
  const netVolume = attrs.netVolume
  const netWeightUnit =
    netWeight && typeof netWeight === "object" && netWeight.unit
      ? extractId(netWeight.unit)
      : ""
  const netVolumeUnit =
    netVolume && typeof netVolume === "object" && netVolume.unit
      ? extractId(netVolume.unit)
      : ""

  const packaging = Array.isArray(product.packaging) ? product.packaging : []
  const firstPkg = packaging[0] as
    | {
        quantity?: number | null
        packageType?: string | null
        itemMarksOuter?: string | null
      }
    | undefined

  return {
    id: product.id,
    item_description: product.groupSequence
      ? `${product.groupSequence}: ${extractName(product.description)}`
      : extractName(product.description),
    net_weight:
      netWeight && typeof netWeight === "object" && netWeight.value
        ? `${netWeight.value} ${netWeightUnit}`.trim()
        : null,
    net_volume:
      netVolume && typeof netVolume === "object" && netVolume.value
        ? `${netVolume.value} ${netVolumeUnit}`.trim()
        : null,
    eligibility_Countries: Array.isArray(product.eligibilityCountries)
      ? product.eligibilityCountries
          .flatMap((c: unknown) => {
            if (typeof c === "string") return [c]
            return [extractId(c), extractName(c)]
          })
          .filter(Boolean)
      : [],
    container_number: container?.containerNumber ?? null,
    container_seals: Array.isArray(container?.seals)
      ? (container.seals as string[])
      : [],
    NZHSC_code: extractId(attrs.NZHSCode) || null,
    productionBatch_ids: Array.isArray(attrs.productionBatchIds)
      ? attrs.productionBatchIds
      : [],
    package_quantity:
      firstPkg?.quantity != null ? String(firstPkg.quantity) : null,
    package_type: firstPkg?.packageType
      ? extractId(firstPkg.packageType)
      : null,
    item_marks: packaging
      .map((pkg: { itemMarksOuter?: string | null }) => pkg?.itemMarksOuter)
      .filter((m: unknown): m is string => Boolean(m) && typeof m === "string"),
    transfer_id: transferId,
    certificate_number: certificateNumber,
    unofficial_commercial_ref:
      typeof product.productReference === "string"
        ? product.productReference
        : null,
    comments: product.productStatement || null,
    sequence_number: product.groupSequence ?? null,
    updated_at: product.updatedAt?.toISOString() ?? null,
    is_exhausted: product.isExhausted ?? false,
  }
}

export const saveTransferProductsToSearch = async (
  tx: DbTransaction,
  id: string,
  collectionName: string,
  options?: { returnDoc?: boolean },
): Promise<void> => {
  const result = await getTransferProductsBulkData(tx, [id])
  const doc = result.get(id)
  if (!doc) return

  if (options?.returnDoc) return doc as never

  const attributes: Record<string, MessageAttributeValue> = {
    collection: { StringValue: collectionName, DataType: "String" },
  }

  await sqs_client.send(
    new SendMessageCommand({
      QueueUrl: queue_url,
      MessageBody: JSON.stringify(doc),
      MessageAttributes: attributes,
    }),
  )
}

export const getTransferProductsBulkData = async (
  tx: DbTransaction,
  ids: readonly string[],
): Promise<Map<string, TransferProductDoc>> => {
  if (ids.length === 0) return new Map()

  // 1. Batch fetch product items (batched to avoid param limit + ensure index usage)
  const BATCH_SIZE = 2000
  const products: Array<{
    id: string
    productGroupId: string
    description: unknown
    attributes: unknown
    packaging: unknown
    eligibilityCountries: unknown
    productReference: string | null
    productStatement: string | null
    groupSequence: number | null
    updatedAt: Date | null
    isExhausted: boolean | null
  }> = []
  for (let i = 0; i < ids.length; i += BATCH_SIZE) {
    const batch = ids.slice(i, i + BATCH_SIZE) as string[]
    const batchRows = await tx
      .select({
        id: coreProductItem.id,
        productGroupId: coreProductItem.productGroupId,
        description: coreProductItem.description,
        attributes: coreProductItem.attributes,
        packaging: coreProductItem.packaging,
        eligibilityCountries: coreProductItem.eligibilityCountries,
        productReference: coreProductItem.productReference,
        productStatement: coreProductItem.productStatement,
        groupSequence: coreProductItem.groupSequence,
        updatedAt: coreProductItem.updatedAt,
        isExhausted: coreProductItem.isExhausted,
      })
      .from(coreProductItem)
      .where(inArray(coreProductItem.id, batch))
    products.push(...batchRows)
  }

  if (products.length === 0) return new Map()

  // 2. Batch fetch containers (batched to avoid seq scan on 30M+ row table)
  const productIds = products.map((p) => p.id)
  const containers = await batchedInQuery<{
    productItemId: string | null
    containerNumber: string | null
    seals: unknown
  }>(tx, coreContainer, coreContainer.productItemId, productIds, {
    productItemId: coreContainer.productItemId,
    containerNumber: coreContainer.containerNumber,
    seals: coreContainer.seals,
  })

  const containersByProduct = new Map<
    string,
    { containerNumber: string | null; seals: unknown }
  >()
  for (const c of containers) {
    if (c.productItemId) containersByProduct.set(c.productItemId, c)
  }

  // 3. Batch fetch transfer requests via product groups (batched for large ID sets)
  const groupIds = [...new Set(products.map((p) => p.productGroupId))]
  const transfers: Array<{
    productGroupId: string
    id: string
    certificateNumber: string
  }> = []
  for (let i = 0; i < groupIds.length; i += BATCH_SIZE) {
    const batch = groupIds.slice(i, i + BATCH_SIZE)
    const batchRows = await tx
      .select({
        productGroupId: transferRequest.productGroupId,
        id: transferRequest.id,
        certificateNumber: transferRequest.certificate_number,
      })
      .from(transferRequest)
      .where(inArray(transferRequest.productGroupId, batch))
    transfers.push(...batchRows)
  }

  const transferByGroup = new Map<
    string,
    { id: string; certificateNumber: string }
  >()
  for (const t of transfers) {
    transferByGroup.set(t.productGroupId, {
      id: t.id,
      certificateNumber: t.certificateNumber,
    })
  }

  // 4. Fetch NZHSCode from classification table
  const nzhscByProduct = await fetchNzhscFromClassification(tx, productIds)

  // 5. Build docs
  const result = new Map<string, TransferProductDoc>()
  for (const product of products) {
    const transfer = transferByGroup.get(product.productGroupId)
    if (!transfer) continue

    const doc = buildTransferProductDoc(
      product as Parameters<typeof buildTransferProductDoc>[0],
      transfer.id,
      transfer.certificateNumber,
      containersByProduct.get(product.id),
      nzhscByProduct,
    )
    result.set(product.id, doc)
  }
  return result
}
